/**
 * Typed, validated access to environment variables.
 * Import `env` everywhere instead of touching `import.meta.env` directly.
 */

function readString(key: keyof ImportMetaEnv, fallback?: string): string {
  const value = import.meta.env[key]
  if (value === undefined || value === '') {
    if (fallback !== undefined) return fallback
    // Fail loud in dev; in prod we degrade to an empty string.
    if (import.meta.env.DEV) {
      console.warn(`[env] Missing required environment variable: ${key}`)
    }
    return ''
  }
  return value
}

function readNumber(key: keyof ImportMetaEnv, fallback: number): number {
  const raw = import.meta.env[key]
  const parsed = raw ? Number(raw) : NaN
  return Number.isFinite(parsed) ? parsed : fallback
}

function readBool(key: keyof ImportMetaEnv, fallback: boolean): boolean {
  const raw = import.meta.env[key]
  if (raw === undefined) return fallback
  return raw === 'true' || raw === '1'
}

export const env = {
  apiBaseUrl: readString('VITE_API_BASE_URL'),
  apiTimeout: readNumber('VITE_API_TIMEOUT', 30_000),
  appName: readString('VITE_APP_NAME', 'AMLiQ | Identity Intelligence'),
  /** Google OAuth Web client ID — empty keeps the Google button disabled. */
  googleClientId: readString('VITE_GOOGLE_CLIENT_ID', ''),
  /** Microsoft Entra app client ID — empty keeps the Microsoft button disabled. */
  microsoftClientId: readString('VITE_MICROSOFT_CLIENT_ID', ''),
  /** Microsoft Entra tenant for the sign-in authority (defaults to "common"). */
  microsoftTenantId: readString('VITE_MICROSOFT_TENANT_ID', ''),
  enableDevtools: readBool('VITE_ENABLE_DEVTOOLS', import.meta.env.DEV),
  /** Dev-only: sign in as a mock user with this UserRole. Empty disables it. */
  devMockRole: readString('VITE_DEV_MOCK_ROLE', ''),
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const

export type Env = typeof env
